import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CarWashPackage } from "../../types/carWash.types";
import {
  getWashPackages,
  washPackageAdd,
  washPackageDelete,
  washPackageUpdate,
} from "../../services/carWashService";
import toast from "react-hot-toast";

interface CarWashState {
  packages: CarWashPackage[];
  isLoading: boolean;
}

const initialState: CarWashState = {
  packages: [],
  isLoading: true,
};

export const getPackages = createAsyncThunk(
  "get/washPackages",
  async (_, { rejectWithValue }) => {
    const { data, error } = (await getWashPackages()) as {
      data: { packages: CarWashPackage[]; message: string };
      error: any;
    };

    if (error) rejectWithValue(error);

    return data;
  }
);

export const updateWashPackage = createAsyncThunk(
  "update/washPackage",
  async ({ wash }: { wash: CarWashPackage }, { rejectWithValue }) => {
    const { data, error } = (await washPackageUpdate(wash)) as {
      data: CarWashPackage | { message: string };
      error: { message: string };
    };

    if (error) {
      toast.error(error.message || "Failed to update wash");
      return rejectWithValue(error);
    }

    return data;
  }
);

export const deleteWashPackage = createAsyncThunk(
  "delete/washPackage",
  async ({ washID }: { washID: string }, { rejectWithValue }) => {
    const { data, error } = (await washPackageDelete(washID)) as {
      data: { message: string; id: string };
      error: { message: string };
    };

    if (error) return rejectWithValue(error);

    return data;
  }
);

export const addWashPackage = createAsyncThunk(
  "add/washPackage",
  async ({ wash }: { wash: Partial<CarWashPackage> }, { rejectWithValue }) => {
    const { data, error } = (await washPackageAdd(wash)) as {
      data: CarWashPackage | { message: string };
      error: { message: string };
    };

    if (error) {
      return rejectWithValue(error);
    }

    return data;
  }
);

export const carWashSlice = createSlice({
  name: "wash",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(getPackages.fulfilled, (state, action) => {
      state.packages = action.payload.packages;
      state.isLoading = false;
    });

    builder.addCase(updateWashPackage.fulfilled, (state, action) => {
      const updatedWash = action.payload as CarWashPackage;
      const index = state.packages.findIndex(
        (wash) => wash._id === updatedWash._id
      );
      if (index !== -1) {
        state.packages[index] = updatedWash;
      }
    });

    builder.addCase(deleteWashPackage.fulfilled, (state, action) => {
      const deletedWashID = action.payload.id;
      state.packages = state.packages.filter(
        (wash) => wash._id !== deletedWashID
      );
      toast.success("Wash package deleted successfully");
    });

    builder.addCase(addWashPackage.fulfilled, (state, action) => {
      const newWash = action.payload as CarWashPackage;
      state.packages.push(newWash);
      toast.success("Wash package added successfully");
    });
  },
});
export default carWashSlice.reducer;
